import { useEffect, useRef, useState } from 'react'
import { ArrowRight, Camera, Check, KeyRound, Link2, Plus, Shield, ShieldCheck, Trash2, Users, Zap } from 'lucide-react'
import { MriButton, MriInput, MriSpinner, cn } from '@mriqbox/ui-kit'
import { InstallApp } from '@/components/InstallApp'
import { PermanentRooms } from '@/components/PermanentRooms'
import { usePermanentRooms } from '@/hooks/usePermanentRooms'
import { PinModal } from '@/components/PinModal'
import { Avatar } from '@/components/Avatar'
import { clearLastRoomCode, formatRoomCode, isCompleteRoomCode, loadLastRoomCode } from '@/lib/format'
import { clearSavedPins, getSavedPin, hasSavedPins, removeSavedPin, savePin } from '@/lib/savedPins'
import { processAvatarFile } from '@/lib/avatar'
import type { SessionError, SessionStatus } from '@/hooks/useRoomSession'

export interface LandingPageProps {
    brand: string
    status: SessionStatus
    error: SessionError | null
    initialNickname: string
    initialCode: string
    avatar: string | null
    onAvatarChange: (dataUrl: string | null) => void
    onCreate: (nickname: string) => void
    onJoin: (code: string, nickname: string, pin?: string) => void
}

const MAX_NICK = 24

export function LandingPage({
    brand,
    status,
    error,
    initialNickname,
    initialCode,
    avatar,
    onAvatarChange,
    onCreate,
    onJoin
}: LandingPageProps) {
    const [nickname, setNickname] = useState(initialNickname)
    const [code, setCode] = useState(initialCode)
    const [tab, setTab] = useState<'quick' | 'permanent'>('quick')

    /**
     * O cartao de confirmacao so aparece pra codigo que chegou de fora (link da
     * sala, ultima sala salva). Enquanto a pessoa digita o campo tem que ficar
     * onde esta: um endereco de sala fixa ja e valido com 3 letras (MIN_SLUG), e
     * trocar o campo pelo cartao nessa hora travava a digitacao no terceiro
     * caractere.
     */
    const [confirming, setConfirming] = useState(() => isCompleteRoomCode(initialCode))

    useEffect(() => {
        if (initialCode) {
            setCode(initialCode)
            setConfirming(isCompleteRoomCode(initialCode))
        }
    }, [initialCode])
    const [pinModal, setPinModal] = useState<{
        open: boolean
        roomCode: string
        roomName?: string
        error?: string | null
    }>({
        open: false,
        roomCode: ''
    })
    const busy = status === 'connecting'
    const trimmed = nickname.trim()
    const permanent = usePermanentRooms()

    // Buttons stay clickable without a name: a grey button that says nothing was the top complaint.
    const [nameMissing, setNameMissing] = useState(false)
    /** What the click wanted to do; Enter in the name field finishes it. */
    const pendingAction = useRef<((name: string) => void) | null>(null)
    const [lastAction, setLastAction] = useState<'create' | 'join' | null>(null)

    const focusName = () => {
        const input = document.getElementById('nickname') as HTMLInputElement | null
        input?.focus()
    }

    const requireName = (action: (name: string) => void) => {
        if (trimmed) {
            action(trimmed)
            return
        }
        pendingAction.current = action
        setNameMissing(true)
        focusName()
    }

    // Arriving without a saved name: the cursor starts where the first step is.
    useEffect(() => {
        if (!initialNickname.trim()) focusName()
    }, [initialNickname])

    // So mostra o botao de esquecer quando ha mesmo algo guardado neste navegador.
    const [canForgetDevice, setCanForgetDevice] = useState(() => hasSavedPins() || !!loadLastRoomCode())
    const [deviceForgotten, setDeviceForgotten] = useState(false)

    useEffect(() => {
        if (!deviceForgotten) return
        const timer = setTimeout(() => setDeviceForgotten(false), 2000)
        return () => clearTimeout(timer)
    }, [deviceForgotten])

    // PIN fica em texto puro no localStorage: isto e a saida pra quem quer
    // desfazer o "lembrar" sem precisar abrir o devtools pra achar a chave.
    const handleForgetDevice = () => {
        clearSavedPins()
        clearLastRoomCode()
        setCanForgetDevice(false)
        setDeviceForgotten(true)
    }

    // Abre o popup de PIN automaticamente se a sala exigir PIN ou se o PIN digitado estiver incorreto
    useEffect(() => {
        if (error?.code === 'pin_required' || error?.code === 'invalid_pin') {
            const activeCode = pinModal.roomCode || code
            if (error.code === 'invalid_pin') {
                removeSavedPin(activeCode)
            }
            setPinModal((current) => ({
                open: true,
                roomCode: activeCode,
                roomName: current.roomName,
                error: error.code === 'invalid_pin' ? 'PIN incorreto. Verifique e tente novamente.' : null
            }))
        }
    }, [error, code, pinModal.roomCode])

    const create = () => requireName((name) => {
        setLastAction('create')
        onCreate(name)
    })

    const handleJoin = (targetCode: string, targetName?: string, directPin?: string) => {
        const cleanCode = targetCode.trim()
        if (!isCompleteRoomCode(cleanCode)) return

        requireName((name) => {
            setLastAction('join')

            // Se já passou o PIN direto (ex: submeteu pelo popup)
            if (directPin !== undefined) {
                onJoin(cleanCode, name, directPin)
                return
            }

            // Verifica se já tem o PIN salvo no localStorage para entrar direto sem perguntar
            const savedPin = getSavedPin(cleanCode)
            if (savedPin) {
                onJoin(cleanCode, name, savedPin)
                return
            }

            // Tenta entrar normalmente (se pedir PIN, o servidor responde e abre o modal)
            setPinModal((curr) => ({ ...curr, roomCode: cleanCode, roomName: targetName }))
            onJoin(cleanCode, name, undefined)
        })
    }

    const handlePinModalSubmit = (submittedPin: string, remember: boolean) => {
        const targetCode = pinModal.roomCode
        if (remember) {
            savePin(targetCode, submittedPin)
        } else {
            removeSavedPin(targetCode)
        }
        setPinModal({ open: false, roomCode: '' })
        handleJoin(targetCode, pinModal.roomName, submittedPin)
    }

    const handleNicknameKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter' || !trimmed) return
        event.preventDefault()
        const action = pendingAction.current
        pendingAction.current = null
        if (action) action(trimmed)
        else if (tab === 'quick' && confirming && isCompleteRoomCode(code)) handleJoin(code)
    }

    const handleAvatarFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        try {
            const dataUrl = await processAvatarFile(file)
            onAvatarChange(dataUrl)
        } catch (err) {
            console.error('Falha ao processar avatar:', err)
        }
        if (e.target) e.target.value = ''
    }

    const showConfirm = tab === 'quick' && confirming && isCompleteRoomCode(code)
    const spinner = <span className="mr-2"><MriSpinner size="sm" /></span>

    /*
     * `overflow-x-hidden`, e nao `overflow-hidden`: num celular deitado a
     * pagina passa da altura da tela, e cortar o eixo vertical junto escondia o
     * formulario de entrar na sala sem deixar rolar ate ele.
     */
    return (
        <main className="flex min-h-full flex-col overflow-x-hidden bg-background select-none">
            {/* Grid so the form sits right under the headline on phones, beside it on desktop. */}
            <div className="mx-auto grid w-full max-w-6xl flex-1 content-center gap-x-12 gap-y-8 px-4 py-8 sm:px-6 sm:py-14 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:grid-rows-[auto_auto]">
                <header className="lg:col-start-1 lg:row-start-1 lg:self-end">
                    <p className="flex items-center gap-2 text-sm">
                        <span className="h-4 w-1 rounded-full bg-primary" aria-hidden />
                        <span className="font-bold uppercase tracking-widest text-foreground">{brand}</span>
                        <span className="text-muted-foreground">screen share</span>
                    </p>

                    {/*
                     * Atalho pro painel, so pra quem tem o cargo.
                     *
                     * A rota segue sem link pra todo o resto — quem nao passa na
                     * checagem nem recebe o campo que acende este botao. Ele
                     * existe porque o app instalado nao tem barra de endereco:
                     * sem um caminho na tela, o operador ficava sem o painel.
                     */}
                    {permanent.isAdmin && (
                        <a
                            href="/admin"
                            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
                        >
                            <Shield className="h-3.5 w-3.5" />
                            Painel do operador
                        </a>
                    )}

                    <h1 key={tab} className="mt-5 text-3xl font-extrabold leading-tight tracking-tight text-foreground animate-in fade-in duration-200 sm:text-4xl lg:mt-6 lg:text-5xl">
                        {tab === 'quick' ? (
                            <>Sua tela, ao vivo,<br />direto para a <span className="text-primary">sala.</span></>
                        ) : (
                            <>Seu link fixo,<br />sempre pronto para <span className="text-primary">transmitir.</span></>
                        )}
                    </h1>
                </header>

                <section
                    aria-label="Entrar ou criar sala"
                    className="w-full lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center"
                >
                    <div className="rounded-2xl border border-border bg-card p-4 shadow-xl sm:p-6">
                        {showConfirm && (
                            <div className="mb-5 rounded-xl border border-primary/40 bg-primary/10 p-3.5 text-center">
                                <p className="text-[11px] font-medium text-muted-foreground">Você foi chamado para a sala</p>
                                <p className="mt-0.5 font-mono text-2xl font-black tracking-[0.2em] text-foreground">{code}</p>
                            </div>
                        )}

                        <div className="flex items-start gap-3.5">
                            {/* Label around the avatar: tapping works on phones, where there is no hover. */}
                            <label
                                className="group/avatar relative mt-5 shrink-0 cursor-pointer rounded-full focus-within:ring-2 focus-within:ring-primary"
                                title="Escolher uma foto (PNG, JPEG, WebP)"
                            >
                                <Avatar nickname={trimmed || '?'} avatar={avatar} size={48} />
                                <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-card bg-primary text-primary-foreground transition-transform group-hover/avatar:scale-110">
                                    <Camera className="h-2.5 w-2.5" />
                                </span>
                                <input
                                    type="file"
                                    accept="image/png,image/jpeg,image/webp,image/*"
                                    className="sr-only"
                                    aria-label="Escolher foto de perfil"
                                    onChange={handleAvatarFile}
                                />
                            </label>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between">
                                    <label htmlFor="nickname" className="text-xs font-semibold text-foreground">
                                        Seu nome
                                    </label>
                                    <div className="flex items-center gap-2">
                                        {avatar && (
                                            <button
                                                type="button"
                                                onClick={() => onAvatarChange(null)}
                                                className="flex items-center gap-0.5 text-[10px] text-muted-foreground transition-colors hover:text-destructive"
                                                title="Remover foto de perfil"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                                Remover foto
                                            </button>
                                        )}
                                        <span className="text-[10px] tabular-nums text-muted-foreground">{nickname.length}/{MAX_NICK}</span>
                                    </div>
                                </div>
                                <MriInput
                                    id="nickname"
                                    value={nickname}
                                    maxLength={MAX_NICK}
                                    autoComplete="nickname"
                                    enterKeyHint="go"
                                    placeholder="Como querem te chamar?"
                                    aria-invalid={nameMissing && !trimmed}
                                    aria-describedby="nickname-hint"
                                    className={cn(
                                        'mt-1.5',
                                        nameMissing && !trimmed && 'border-destructive ring-1 ring-destructive focus-visible:ring-destructive'
                                    )}
                                    onChange={(event) => {
                                        setNickname(event.target.value)
                                        if (event.target.value.trim()) setNameMissing(false)
                                    }}
                                    onKeyDown={handleNicknameKey}
                                />
                                <p
                                    id="nickname-hint"
                                    role={nameMissing && !trimmed ? 'alert' : undefined}
                                    className={cn(
                                        'mt-1.5 text-[11px]',
                                        nameMissing && !trimmed ? 'font-semibold text-destructive' : 'text-muted-foreground'
                                    )}
                                >
                                    {nameMissing && !trimmed
                                        ? 'Coloque seu nome pra continuar.'
                                        : 'É assim que você aparece pra sala.'}
                                </p>
                            </div>
                        </div>

                        {/* Abas Segmentadas: Sala Rápida vs Salas Fixas (Aparece quando login Discord estiver disponível no servidor) */}
                        {permanent.available && (
                            <div className="mt-5 flex rounded-xl border border-border/50 bg-muted/60 p-1" role="tablist">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === 'quick'}
                                    onClick={() => setTab('quick')}
                                    className={cn(
                                        'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all',
                                        tab === 'quick'
                                            ? 'bg-card text-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    <Zap className="h-3.5 w-3.5 text-primary" />
                                    Sala Rápida
                                </button>

                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={tab === 'permanent'}
                                    onClick={() => setTab('permanent')}
                                    className={cn(
                                        'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all',
                                        tab === 'permanent'
                                            ? 'bg-card text-foreground shadow-sm'
                                            : 'text-muted-foreground hover:text-foreground'
                                    )}
                                >
                                    <Link2 className="h-3.5 w-3.5 text-emerald-500" />
                                    Salas Fixas
                                    {permanent.rooms.length > 0 && (
                                        <span className="rounded-full bg-primary/20 px-1.5 text-[10px] font-bold text-primary">
                                            {permanent.rooms.length}
                                        </span>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Conteúdo da Aba: Sala Rápida */}
                        {tab === 'quick' && (
                            <div className="mt-5 space-y-4 animate-in fade-in duration-150">
                                {showConfirm ? (
                                    <div className="space-y-3">
                                        <MriButton
                                            type="button"
                                            className="h-11 w-full text-sm font-bold shadow-md shadow-primary/20"
                                            disabled={busy}
                                            onClick={() => handleJoin(code)}
                                        >
                                            {busy && lastAction === 'join' ? spinner : <ArrowRight className="mr-2 h-4 w-4" />}
                                            Entrar na sala
                                        </MriButton>

                                        <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCode('')
                                                    setConfirming(false)
                                                    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
                                                        window.history.replaceState(null, '', '/')
                                                    }
                                                }}
                                                className="py-1 transition-colors hover:text-foreground"
                                            >
                                                Digitar outro código
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={create}
                                                className="flex items-center gap-1 py-1 font-semibold text-primary hover:underline disabled:opacity-50"
                                            >
                                                {busy && lastAction === 'create' ? <MriSpinner size="sm" /> : <Plus className="h-3.5 w-3.5" />}
                                                Criar nova sala
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div>
                                            <MriButton
                                                type="button"
                                                className="h-11 w-full font-semibold"
                                                disabled={busy}
                                                onClick={create}
                                            >
                                                {busy && lastAction === 'create' ? spinner : <Plus className="mr-2 h-4 w-4" />}
                                                Criar sala instantânea
                                            </MriButton>
                                            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
                                                Sala temporária, com link pra mandar pros outros
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
                                            <span className="h-px flex-1 bg-border" aria-hidden />
                                            ou entrar numa sala
                                            <span className="h-px flex-1 bg-border" aria-hidden />
                                        </div>

                                        <form
                                            className="space-y-1.5"
                                            onSubmit={(event) => {
                                                event.preventDefault()
                                                handleJoin(code)
                                            }}
                                        >
                                            <label htmlFor="code" className="text-xs font-semibold text-foreground">
                                                Código da sala
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <MriInput
                                                    id="code"
                                                    value={code}
                                                    placeholder="XXXX-XXXX"
                                                    autoComplete="off"
                                                    autoCapitalize="characters"
                                                    spellCheck={false}
                                                    enterKeyHint="go"
                                                    className="font-mono tracking-[0.15em]"
                                                    onChange={(event) => setCode(formatRoomCode(event.target.value))}
                                                />
                                                <MriButton
                                                    type="submit"
                                                    variant="outline"
                                                    className="shrink-0"
                                                    disabled={!isCompleteRoomCode(code) || busy}
                                                >
                                                    {busy && lastAction === 'join' ? spinner : null}
                                                    Entrar
                                                    <ArrowRight className="ml-2 h-4 w-4" />
                                                </MriButton>
                                            </div>
                                            <p className="text-[11px] text-muted-foreground">
                                                Pode colar o link que te mandaram.
                                            </p>
                                        </form>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Conteúdo da Aba: Salas Fixas */}
                        {tab === 'permanent' && (
                            <div className="animate-in fade-in duration-150">
                                <PermanentRooms
                                    loading={permanent.loading}
                                    available={permanent.available}
                                    user={permanent.user}
                                    rooms={permanent.rooms}
                                    error={permanent.error}
                                    currentNickname={nickname}
                                    avatar={avatar}
                                    onSelectRoom={(roomCode) => setCode(roomCode)}
                                    onRequestPin={(roomCode, roomName) => requireName(() => {
                                        setPinModal({ open: true, roomCode, roomName, error: null })
                                    })}
                                    onJoinRoom={(roomCode, roomPin) => {
                                        handleJoin(roomCode, undefined, roomPin)
                                    }}
                                    onCreate={(name, newPin) => void permanent.createRoom(name, newPin)}
                                    onUpdate={(roomCode, fields) => void permanent.updateRoom(roomCode, fields)}
                                    onDelete={(roomCode) => {
                                        removeSavedPin(roomCode)
                                        void permanent.deleteRoom(roomCode)
                                    }}
                                />
                            </div>
                        )}

                        {error && !pinModal.open && (
                            <p role="alert" className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                                {error.message}
                            </p>
                        )}

                        {/* PIN salvo fica visivel em texto puro pra quem mexer no navegador
                            de quem visita — este e o jeito de desfazer o "lembrar". */}
                        {(canForgetDevice || deviceForgotten) && (
                            <button
                                type="button"
                                onClick={handleForgetDevice}
                                disabled={deviceForgotten}
                                title="Apaga os PINs salvos e o último código lembrado neste navegador"
                                className="mt-4 flex w-full items-center justify-center gap-1.5 border-t border-border/60 pt-3 text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default"
                            >
                                {deviceForgotten
                                    ? <Check className="h-3 w-3 text-primary" />
                                    : <Trash2 className="h-3 w-3" />}
                                {deviceForgotten ? 'Dispositivo esquecido' : 'Esquecer PIN e código salvos neste dispositivo'}
                            </button>
                        )}
                    </div>
                </section>

                <div key={`${tab}-details`} className="animate-in fade-in duration-200 lg:col-start-1 lg:row-start-2 lg:self-start">
                    {tab === 'quick' ? (
                        <>
                            <p className="max-w-md leading-relaxed text-muted-foreground">
                                Crie uma sala em um clique, mande o link e transmita a tela pelo navegador.
                                Sem instalar nada, sem cadastro.
                            </p>
                            <ul className="mt-6 space-y-3 text-sm">
                                <Feature icon={Zap}>Sala instantânea, entra por código ou link</Feature>
                                <Feature icon={ShieldCheck}>O vídeo vai de um navegador ao outro, sem passar pelo servidor</Feature>
                                <Feature icon={Users}>Várias pessoas transmitem ao mesmo tempo, em mosaico ou destaque</Feature>
                            </ul>
                        </>
                    ) : (
                        <>
                            <p className="max-w-md leading-relaxed text-muted-foreground">
                                Endereços que não mudam, pra reunião recorrente, stream ou resenha com os amigos.
                            </p>
                            <ul className="mt-6 space-y-3 text-sm">
                                <Feature icon={Link2}>Link permanente: o endereço nunca muda e dura 7 dias após o uso</Feature>
                                <Feature icon={KeyRound}>Controle com PIN: proteja sua sala com senha quando quiser</Feature>
                                <Feature icon={Users}>Livre para convidados: qualquer pessoa entra pelo link, sem login</Feature>
                            </ul>
                        </>
                    )}

                    {/* Fora das duas abas: instalar o app vale pra qualquer uso. */}
                    <InstallApp />
                </div>
            </div>

            {/* Popup / Modal de PIN com opção de salvar senha */}
            <PinModal
                open={pinModal.open}
                roomCode={pinModal.roomCode}
                roomName={pinModal.roomName}
                error={pinModal.error}
                onClose={() => setPinModal({ open: false, roomCode: '' })}
                onSubmit={handlePinModalSubmit}
            />
        </main>
    )
}

function Feature({ icon: Icon, children }: { icon: typeof Zap; children: React.ReactNode }) {
    return (
        <li className="flex items-center gap-3 text-muted-foreground">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/70 bg-card/60">
                <Icon className="h-3.5 w-3.5 text-primary" />
            </span>
            {children}
        </li>
    )
}
